import { TokenStore } from "../token-store"

/**
 * Filters tokens based on whether they are verified (isSwapToken)
 * @param tokens - Array of tokens to filter
 * @param onlyVerifiedTokens - If true, only return tokens where isSwapToken is true
 * @returns Filtered array of tokens
 */
export const filterVerifiedTokens = (tokens: Array<TokenStore>, onlyVerifiedTokens: boolean): Array<TokenStore> => {
  if (onlyVerifiedTokens === false) {
    return tokens
  }
  return tokens.filter((token) => token.isSwapToken)
}

/**
 * Sorts tokens by balance value in USD (descending)
 * @param tokens - Array of tokens to sort
 * @returns Sorted array of tokens
 */
export const sortTokensByBalanceValueUSD = (tokens: Array<TokenStore>): Array<TokenStore> => {
  return [...tokens].sort((a, b) => {
    return a.balanceValueUSD > b.balanceValueUSD ? -1 : 1
  })
}

/**
 * Filters and sorts tokens for display in token dialogs
 * @param tokens - Array of tokens to process
 * @param onlyVerifiedTokens - If true, only return verified tokens
 * @returns Filtered and sorted array of tokens
 */
export const filterAndSortTokens = (tokens: Array<TokenStore>, onlyVerifiedTokens: boolean): Array<TokenStore> => {
  const filtered = filterVerifiedTokens(tokens, onlyVerifiedTokens)
  return sortTokensByBalanceValueUSD(filtered)
}

/**
 * Filters tokens to get listed tokens (isSwapToken or native token)
 * @param tokens - Array of tokens to filter
 * @param nativeToken - Native token TokenStore instance
 * @returns Filtered array of listed tokens
 */
export const filterListedTokens = (tokens: Array<TokenStore>, nativeToken: TokenStore): Array<TokenStore> => {
  return tokens.filter((token) => {
    return token.isSwapToken || token.address.equals(nativeToken.address)
  })
}

/**
 * Filters tokens to get unlisted tokens (not isSwapToken and not native token)
 * @param tokens - Array of tokens to filter
 * @param nativeToken - Native token TokenStore instance
 * @returns Filtered array of unlisted tokens
 */
export const filterUnlistedTokens = (tokens: Array<TokenStore>, nativeToken: TokenStore): Array<TokenStore> => {
  return tokens.filter((token) => {
    const isNative = token.address.equals(nativeToken.address)
    return !isNative && !token.isSwapToken
  })
}

/**
 * Filters tokens by search string (address, name, or symbol)
 * @param tokens - Array of tokens to filter
 * @param searchString - Search string to match against
 * @param nativeTokenSymbol - Symbol of native token (for exact match)
 * @param wrappedNativeSymbol - Symbol of wrapped native token (for exact match)
 * @returns Filtered array of tokens
 */
export const filterTokensBySearch = (
  tokens: Array<TokenStore>,
  searchString: string | null | undefined,
  nativeTokenSymbol?: string,
  wrappedNativeSymbol?: string,
): Array<TokenStore> => {
  if (!searchString || searchString === "") {
    return tokens
  }

  const nativeTokenSymbolLower = nativeTokenSymbol?.toLowerCase() ?? ""
  const wrappedNativeSymbolLower = wrappedNativeSymbol?.toLowerCase() ?? ""
  const searchLower = searchString.toLowerCase()

  // If searching exactly for native token symbol, only match that
  if (nativeTokenSymbolLower && nativeTokenSymbolLower === searchLower) {
    return tokens.filter((token) => token.symbol.toLowerCase() === nativeTokenSymbolLower)
  }

  // If searching exactly for wrapped native token symbol, only match that
  if (wrappedNativeSymbolLower && wrappedNativeSymbolLower === searchLower) {
    return tokens.filter((token) => token.symbol.toLowerCase() === wrappedNativeSymbolLower)
  }

  // Otherwise, fuzzy match on address, name, or symbol
  return tokens.filter((token) => {
    const address = token.address.toViemAddress().toLowerCase()
    const name = token.name.toLowerCase()
    const symbol = token.symbol.toLowerCase()

    return address.includes(searchLower) || name.includes(searchLower) || symbol.includes(searchLower)
  })
}
